import {
  ConfigOf,
  Element,
  InstallSpec,
  ItemComponent,
  Option,
} from "@p0security/shared/install/types";
import { Typography } from "antd";
import { useNavigateWithSearchParams } from "components/Assessment/hooks/useNavigateWithSearchParams";
import {
  FrontendComponentInstaller,
  FrontendInstallContext,
  FrontendInstaller,
} from "install/types";
import { capitalize, lowerCase, omit } from "lodash";
import { useCallback, useMemo } from "react";

import { ComponentProps, NEW_SENTINEL } from "./Component";
import { configPath } from "./Config";
import { ConfigForm } from "./ConfigForm";

export type NewProps<T extends InstallSpec> = ComponentProps<T> & {
  component: ItemComponent<any>;
  context: FrontendInstallContext<ConfigOf<T>>;
  identifier?: (key: string) => string;
};

/** Recursively extracts "new"-step fields from a component schema */
const pickNew = <S extends Record<string, Element>>(
  schema: S
): Record<string, Element> => {
  const output: Record<string, Element> = {};
  for (const [key, item] of Object.entries(schema)) {
    if (item.step !== "new") continue;
    if (item.type !== "select") {
      output[key] = item;
    } else {
      const options: Record<string, Option<any>> = {};
      for (const [k, option] of Object.entries(item.options)) {
        options[k] = { ...option, schema: pickNew(option.schema) };
      }
      output[key] = { ...item, options };
    }
  }
  return output;
};

export const New = <T extends InstallSpec>(props: NewProps<T>) => {
  const {
    authFetch,
    component,
    componentKey,
    config,
    context,
    installer,
    itemKind,
    identifier,
  } = props;
  const navigate = useNavigateWithSearchParams();
  const kind = itemKind(componentKey);
  const schema = {
    id: {
      ...omit(component, "schema"),
      // TODO - ENG-4818: make more specific eg Account Number for AWS, Project Name for GCP
      label: `${lowerCase(kind) === kind ? capitalize(kind) : kind} ${
        identifier?.(componentKey) ?? "identifier"
      }`,
      description: undefined,
      step: "new",
    },
    ...pickNew(component.schema),
  };
  const submit = useCallback(
    async (item: object & { id: string }) => {
      if (!config) {
        const createResponse = await authFetch(
          configPath({
            integration: props.integration,
            componentKey: undefined,
            id: undefined,
          }),
          // TODO: root config
          { method: "POST", json: {} }
        );
        if (!createResponse) return;
      }
      const response = await authFetch(
        configPath({ ...props, id: encodeURIComponent(item.id) }),
        {
          method: "PUT",
          json: omit(item, "id"),
        }
      );
      if (response?.ok) {
        navigate(`../${encodeURIComponent(item.id)}`, { relative: "path" });
      }
    },
    [authFetch, config, navigate, props]
  );

  const idInstaller = useMemo(() => {
    const componentInstaller = (installer?.[componentKey] ??
      {}) as FrontendInstaller<any>;
    return {
      items: { id: componentInstaller, ...componentInstaller.items },
    } as FrontendComponentInstaller<any>;
  }, [componentKey, installer]);

  return (
    <>
      <Typography.Title level={5}>
        Installing a new {itemKind(componentKey)}
      </Typography.Title>
      <Typography.Paragraph>
        {component.type === "string" ? "Enter" : "Select"} the{" "}
        {identifier?.(componentKey) ?? "identifier"} of the{" "}
        {itemKind(componentKey)} to install, then click &quot;Next&quot;:
      </Typography.Paragraph>
      <ConfigForm
        config={{}}
        context={context}
        id={NEW_SENTINEL}
        installer={idInstaller}
        isFetching={props.isFetching}
        schema={schema}
        setConfig={submit}
        step="new"
      />
    </>
  );
};
